import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { Job } from '../generated/gql-types';
import { DEFAULT_JOB_TIMEZONE, formatInJobTimezone } from './jobTimezone.util';

dayjs.extend(utc);
dayjs.extend(timezone);
import { TimesheetSubmissionFrequency } from '@core/coreLoopConfiguration/coreLoopConfiguration.types';

/**
 * Stored app date answers: unix epoch in milliseconds (e.g. from `dayjs(...).valueOf()`).
 * No legacy seconds conversion — the API/DB use ms only.
 */
export function parseStoredEpochMs(
  value: string | number | null | undefined,
): number {
  if (value === '' || value === null || value === undefined) {
    return NaN;
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return NaN;
  }
  return n;
}

/** Same as {@link parseStoredEpochMs} but empty or invalid input becomes `0` (for pickers / display). */
export function parseStoredEpochMsOrZero(
  value: string | number | null | undefined,
): number {
  const ms = parseStoredEpochMs(value);
  return Number.isFinite(ms) ? ms : 0;
}

const MS_PER_UTC_DAY = 86_400_000;
const ISO_CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

const DOB_TIME_ZONE_BY_STATE: Record<string, string> = {
  ACT: 'Australia/Sydney',
  NSW: 'Australia/Sydney',
  NT: 'Australia/Darwin',
  QLD: 'Australia/Brisbane',
  SA: 'Australia/Adelaide',
  TAS: 'Australia/Hobart',
  VIC: 'Australia/Melbourne',
  WA: 'Australia/Perth',
  'AUSTRALIAN CAPITAL TERRITORY': 'Australia/Sydney',
  'NEW SOUTH WALES': 'Australia/Sydney',
  'NORTHERN TERRITORY': 'Australia/Darwin',
  QUEENSLAND: 'Australia/Brisbane',
  'SOUTH AUSTRALIA': 'Australia/Adelaide',
  TASMANIA: 'Australia/Hobart',
  VICTORIA: 'Australia/Melbourne',
  'WESTERN AUSTRALIA': 'Australia/Perth',
};

export type DobAddress = {
  state?: string | null;
  countryLabel?: string | null;
  countryValue?: string | null;
};

function isUtcMidnight(ms: number): boolean {
  return Number.isFinite(ms) && ms % MS_PER_UTC_DAY === 0;
}

/** Address zone for a legacy dob that still has a clock time. Falls back to Sydney. */
function resolveDobTimeZone(address?: DobAddress | null): string {
  const countryParts = [address?.countryValue, address?.countryLabel]
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim().toUpperCase())
    .filter((value) => value.length > 0);
  const inAustralia = countryParts.some((value) =>
    ['AU', 'AUS', 'AUSTRALIA'].includes(value),
  );
  const inNewZealand = countryParts.some((value) =>
    ['NZ', 'NZL', 'NEW ZEALAND'].includes(value),
  );
  if (inNewZealand && !inAustralia) {
    return 'Pacific/Auckland';
  }
  const rawState = (address?.state ?? '').trim().toUpperCase();
  return DOB_TIME_ZONE_BY_STATE[rawState] ?? DEFAULT_JOB_TIMEZONE;
}

/** Midnight UTC for the calendar day a date picker showed. */
export function utcMidnightFromObservedDate(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
}

/** `YYYY-MM-DD` saved as midnight UTC. 13 October 2001 is `1002931200000`. */
export function utcMidnightFromIsoDate(dateString: string): string {
  if (!ISO_CALENDAR_DATE.test(dateString)) {
    return '';
  }
  const parsed = dayjs.utc(dateString);
  if (!parsed.isValid() || parsed.format('YYYY-MM-DD') !== dateString) {
    return '';
  }
  return String(parsed.valueOf());
}

/**
 * Calendar day of a stored dob.
 * Midnight UTC values use the UTC day. Older values that still carry a clock
 * time use the address timezone, so `1002931056000` is 13 October 2001 in Australia.
 */
export function formatDobCalendarIso(
  value: string | number | null | undefined,
  address?: DobAddress | null,
): string {
  if (typeof value === 'string' && ISO_CALENDAR_DATE.test(value.trim())) {
    return value.trim();
  }
  const ms = parseStoredEpochMs(value);
  if (!Number.isFinite(ms)) {
    return '';
  }
  const timeZone = isUtcMidnight(ms) ? 'UTC' : resolveDobTimeZone(address);
  const formatted = dayjs(ms).tz(timeZone).format('YYYY-MM-DD');
  return formatted === 'Invalid Date' ? '' : formatted;
}

/** Long calendar day, for example `13 October 2001`. */
export function formatDobCalendarDisplay(
  value: string | number | null | undefined,
  address?: DobAddress | null,
): string {
  const isoDate = formatDobCalendarIso(value, address);
  const utcMs = utcMidnightFromIsoDate(isoDate);
  if (!utcMs) {
    return '';
  }
  return dayjs.utc(Number(utcMs)).format('D MMMM YYYY');
}

/** Local `Date` whose calendar day matches the resolved dob, for a device wheel. */
export function localDateForDobCalendarDay(
  value: string | number | null | undefined,
  address?: DobAddress | null,
): Date | null {
  const isoDate = formatDobCalendarIso(value, address);
  if (!isoDate) {
    return null;
  }
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function dayjsFromStoredEpochMs(
  value: string | number | null | undefined,
): Dayjs {
  return dayjs(parseStoredEpochMs(value));
}

/**
 * Convert start datetime(unix timestamp) and enddatetime(unix timestamp) to a readable format 09:00 - 15:00
 * @param startDateTime
 * @param endDateTime
 * @returns
 */
export const convertDateTimeToReadableFormat = (
  startDateTime: string,
  endDateTime: string,
  format: 'HH:mm' | 'HH:mm - HH:mm',
  jobOrTimezone?: Pick<Job, 'timezone'> | string | null,
) => {
  const start = formatInJobTimezone(startDateTime, jobOrTimezone, 'HH:mm');
  const end = formatInJobTimezone(endDateTime, jobOrTimezone, 'HH:mm');
  if (format === 'HH:mm') {
    return `${start} – ${end}`;
  }
  const hours = computeShiftDurationHoursFromUnixMs(startDateTime, endDateTime);
  return `${start} – ${end} (${formatDurationHours(hours)} hrs)`;
};

/** Overnight-aware span between two clock times on the same logical shift day. */
export function computeShiftDurationHours(start: Dayjs, end: Dayjs): number {
  let hours = end.diff(start, 'hour', true);
  if (hours < 0) {
    hours = end.add(1, 'day').diff(start, 'hour', true);
  }
  return hours;
}

export function computeShiftDurationHoursFromUnixMs(
  startDateTime: string | number,
  endDateTime: string | number,
): number {
  const start = dayjs(parseStoredEpochMs(startDateTime));
  const end = dayjs(parseStoredEpochMs(endDateTime));
  if (!start.isValid() || !end.isValid()) {
    return NaN;
  }
  return computeShiftDurationHours(start, end);
}

/** Display hours with two decimal places (e.g. shift range labels). */
export function formatDurationHours(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) {
    return '0.00';
  }
  return hours.toFixed(2);
}

/** Ticket format: `8h 30m`, `8h 00m`. Hours unpadded, minutes always two digits. */
export function formatDurationHhMm(totalMinutes: number): string {
  const safe =
    !Number.isFinite(totalMinutes) || totalMinutes < 0
      ? 0
      : Math.round(totalMinutes);
  const hours = Math.floor(safe / 60);
  const minutes = safe % 60;
  return `${hours}h ${String(minutes).padStart(2, '0')}m`;
}

/**
 * Shift length from two clock faces. Overnight (end clock before start clock)
 * rolls forward one day. Returns null when either clock is missing.
 * `HH:mm` strings, e.g. web create form.
 */
export function formatShiftClockDurationHhMm(
  startTime: string | null | undefined,
  endTime: string | null | undefined,
): string | null {
  if (!startTime || !endTime) return null;
  const [sh, sm] = startTime.split(':').map((n) => Number(n));
  const [eh, em] = endTime.split(':').map((n) => Number(n));
  if (![sh, sm, eh, em].every((n) => Number.isFinite(n))) return null;

  const start = dayjs().hour(sh).minute(sm).second(0).millisecond(0);
  let end = dayjs().hour(eh).minute(em).second(0).millisecond(0);
  if (end.isBefore(start)) {
    end = end.add(1, 'day');
  }
  return formatDurationHhMm(end.diff(start, 'minute'));
}

export type FormatMinutesForDisplayOptions = {
  includeUnit?: boolean;
};

/**
 * Format a duration in minutes for display.
 * Under 60 minutes stays as mins (non-integers are rounded).
 * 60 minutes and over becomes hours, max 2 decimals, trailing zeros stripped.
 */
export function formatMinutesForDisplay(
  minutes: number,
  options?: FormatMinutesForDisplayOptions,
): string {
  const includeUnit = options?.includeUnit ?? false;
  const safeMinutes = !Number.isFinite(minutes) || minutes < 0 ? 0 : minutes;

  if (safeMinutes >= 60) {
    const hours = Number((safeMinutes / 60).toFixed(2));
    if (!includeUnit) {
      return String(hours);
    }
    return hours === 1 ? `${hours} hour` : `${hours} hours`;
  }

  const roundedMinutes = Math.round(safeMinutes);
  if (!includeUnit) {
    return String(roundedMinutes);
  }
  return roundedMinutes === 1
    ? `${roundedMinutes} min`
    : `${roundedMinutes} mins`;
}

/**
 * @param startDateTime unix timestamp string
 * @param endDateTime unix timestamp string
 * @returns diff in hours as a string (two decimal places)
 */
export const durationInHours = (startDateTime: string, endDateTime: string) => {
  return formatDurationHours(
    computeShiftDurationHoursFromUnixMs(startDateTime, endDateTime),
  );
};

/**
 * Get the duration in hours
 * @param startDate unix timestamp string
 * @param endDate unix timestamp string
 * @returns exmaple: ` (6h)`
 */
export const getDuration = (startDate: string, endDate: string) => {
  if (startDate && endDate) {
    const hours = computeShiftDurationHoursFromUnixMs(startDate, endDate);
    return ` (${formatDurationHours(hours)} hrs)`;
  }
  return '';
};

/**
 * Round to next 15-minute increment
 * @param date
 * @returns
 */
export const roundToNext15Minutes = (date: Date): Date => {
  const rounded = new Date(date);
  const minutes = rounded.getMinutes();
  const remainder = minutes % 15;

  if (remainder === 0) {
    // Already on a 15-minute mark, add 15 minutes
    rounded.setMinutes(minutes + 15);
  } else {
    // Round up to next 15-minute mark
    rounded.setMinutes(minutes + (15 - remainder));
  }

  // Reset seconds and milliseconds
  rounded.setSeconds(0);
  rounded.setMilliseconds(0);

  return rounded;
};

export const millisecondsToMinutes = (milliseconds: number) => {
  return milliseconds / 60000;
};

export const minutesToMilliseconds = (minutes: number) => {
  return minutes * 60000;
};

/**
 * Convert a time string (HH:mm) to Unix timestamp using a base date
 * @param timeString - Time in HH:mm format (e.g., "09:30")
 * @param baseDateTimestamp - Unix timestamp string to use as the base date
 * @returns Unix timestamp string
 */
export const timeStringToTimestamp = (
  timeString: string,
  baseDateTimestamp: string,
): string => {
  const [hours, minutes] = timeString.split(':').map(Number);
  const baseDate = dayjs(Number(baseDateTimestamp));
  return baseDate
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0)
    .valueOf()
    .toString();
};
